import { Box, Collapse } from '@mui/material';
import { Ban, ChevronDown } from 'lucide-react';
import { ReactNode } from 'react';
import {
  categoryChevronSx,
  CategoryCount,
  CategoryHeaderButton,
  CategoryName,
  GROUP_COLLAPSE_MS,
  GroupBody,
  groupCollapseSx,
  RestrictionBadge,
} from './policy-category-group.styles';

interface PolicyCategoryGroupSectionProps {
  category: string;
  total: number;
  restrictionCount: number;
  open: boolean;
  onToggle: () => void;
  isFirst?: boolean;
  /** Fires once the open/close motion has settled, for callers that measure. */
  onTransitionEnd?: () => void;
  children: ReactNode;
}

const bodyIdFor = (category: string) =>
  `policy-category-${category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

/**
 * The header and container shared by both policy surfaces. `unmountOnExit` keeps
 * a folded group out of the DOM, so it costs nothing and reads unambiguously as
 * absent to a reader or a test.
 */
export const PolicyCategoryGroupSection = ({
  category,
  total,
  restrictionCount,
  open,
  onToggle,
  isFirst = false,
  onTransitionEnd,
  children,
}: PolicyCategoryGroupSectionProps) => {
  const bodyId = bodyIdFor(category);

  return (
    <>
      <CategoryHeaderButton
        type="button"
        disableRipple
        open={open}
        isFirst={isFirst}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={bodyId}
        data-testid="policy-category-header"
        data-category={category}
      >
        <CategoryName>{category}</CategoryName>

        {restrictionCount > 0 ? (
          <RestrictionBadge
            data-testid="policy-category-restriction-badge"
            aria-label={`${restrictionCount} restriction policies`}
          >
            <Ban size={10} aria-hidden />
            {restrictionCount}
          </RestrictionBadge>
        ) : null}

        <CategoryCount>{total}</CategoryCount>

        <Box aria-hidden sx={categoryChevronSx(open)}>
          <ChevronDown size={15} />
        </Box>
      </CategoryHeaderButton>

      <Collapse
        in={open}
        timeout={GROUP_COLLAPSE_MS}
        unmountOnExit
        sx={groupCollapseSx}
        onEntered={onTransitionEnd}
        onExited={onTransitionEnd}
      >
        <GroupBody
          id={bodyId}
          role="group"
          aria-label={category}
          data-testid="policy-category-body"
          data-category={category}
        >
          {children}
        </GroupBody>
      </Collapse>
    </>
  );
};
